// ============================================================================
// HIERARCHICAL CONFIG FRAMEWORK — ENDPOINTS
// ============================================================================
// Frontend endpoint builders for the F2 backend surface:
//   /api/v1/config-groups        (CRUD)
//   /api/v1/config-definitions   (CRUD)
//   /api/v1/config-scopes        (CRUD)
//   /api/v1/config-values        (CRUD)
//   /api/v1/config-list-items    (CRUD + reorder)
//   /api/v1/config/resolve       (single-key diagnostic)
//   /api/v1/config/effective     (group batch resolve)
//
// buildEndpoint()  → adds /v1 prefix (do NOT double-prefix via buildEndpointWithQuery).
// buildEndpointWithQuery() → wraps ALREADY-versioned path with a query string.
// ============================================================================

import { buildEndpoint, buildEndpointWithQuery } from './base.endpoints';

// ─── config-groups ────────────────────────────────────────────────────────

export const configGroupEndpoints = {
  list: () => buildEndpoint('/config-groups'),
  byId: (id: string) => buildEndpoint(`/config-groups/${id}`),
  create: () => buildEndpoint('/config-groups'),
  update: (id: string) => buildEndpoint(`/config-groups/${id}`),
  delete: (id: string) => buildEndpoint(`/config-groups/${id}`),
} as const;

// ─── config-definitions ───────────────────────────────────────────────────

export const configDefinitionEndpoints = {
  list: (groupId?: string, search?: string) => {
    const base = buildEndpoint('/config-definitions');
    const query: Record<string, string> = {};
    if (groupId) query.groupId = groupId;
    if (search) query.search = search;
    return Object.keys(query).length ? buildEndpointWithQuery(base, query) : base;
  },
  byId: (id: string) => buildEndpoint(`/config-definitions/${id}`),
  create: () => buildEndpoint('/config-definitions'),
  update: (id: string) => buildEndpoint(`/config-definitions/${id}`),
  delete: (id: string) => buildEndpoint(`/config-definitions/${id}`),
} as const;

// ─── config-scopes ────────────────────────────────────────────────────────

export const configScopeEndpoints = {
  list: (scopeType?: string, parentScopeId?: string) => {
    const base = buildEndpoint('/config-scopes');
    const query: Record<string, string> = {};
    if (scopeType) query.scopeType = scopeType;
    if (parentScopeId) query.parentScopeId = parentScopeId;
    return Object.keys(query).length ? buildEndpointWithQuery(base, query) : base;
  },
  byId: (id: string) => buildEndpoint(`/config-scopes/${id}`),
  create: () => buildEndpoint('/config-scopes'),
  update: (id: string) => buildEndpoint(`/config-scopes/${id}`),
  delete: (id: string) => buildEndpoint(`/config-scopes/${id}`),
} as const;

// ─── config-values ────────────────────────────────────────────────────────

export const configValueEndpoints = {
  list: (definitionId?: string, scopeId?: string) => {
    const base = buildEndpoint('/config-values');
    const query: Record<string, string> = {};
    if (definitionId) query.definitionId = definitionId;
    if (scopeId) query.scopeId = scopeId;
    return Object.keys(query).length ? buildEndpointWithQuery(base, query) : base;
  },
  byId: (id: string) => buildEndpoint(`/config-values/${id}`),
  create: () => buildEndpoint('/config-values'),
  update: (id: string) => buildEndpoint(`/config-values/${id}`),
  delete: (id: string) => buildEndpoint(`/config-values/${id}`),
} as const;

// ─── config-list-items ────────────────────────────────────────────────────

export const configListItemEndpoints = {
  /** Both filters required — list items are meaningful only in a (definition, scope) pair. */
  list: (definitionId: string, scopeId: string) => {
    const base = buildEndpoint('/config-list-items');
    return buildEndpointWithQuery(base, { definitionId, scopeId });
  },
  byId: (id: string) => buildEndpoint(`/config-list-items/${id}`),
  create: () => buildEndpoint('/config-list-items'),
  update: (id: string) => buildEndpoint(`/config-list-items/${id}`),
  delete: (id: string) => buildEndpoint(`/config-list-items/${id}`),
  reorder: () => buildEndpoint('/config-list-items/reorder'),
} as const;

// ─── /config/resolve + /config/effective ──────────────────────────────────

export const configDiagnosticEndpoints = {
  /** GET /api/v1/config/resolve?configKey=…&voucherTypeId=… */
  resolve: (configKey: string, voucherTypeId?: string) => {
    const base = buildEndpoint('/config/resolve');
    const query: Record<string, string> = { configKey };
    if (voucherTypeId) query.voucherTypeId = voucherTypeId;
    return buildEndpointWithQuery(base, query);
  },
  /** GET /api/v1/config/effective?groupKey=… */
  effective: (groupKey: string) => {
    const base = buildEndpoint('/config/effective');
    return buildEndpointWithQuery(base, { groupKey });
  },
} as const;
